import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import TemplateEditor from '../../components/admin/TemplateEditor';
import EmailPreview from '../../components/admin/EmailPreview';

interface CohortSummary {
    cohort: string;
    total_users: number;
    eligible_users: number;
    blocked_by_frequency: number;
}

const GrowthDashboard: React.FC = () => {
    const [summaries, setSummaries] = useState<CohortSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCohort, setSelectedCohort] = useState<string | null>(null);
    const [templates, setTemplates] = useState<string[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
    const [templateContent, setTemplateContent] = useState<string>('');
    const [recipientsCsv, setRecipientsCsv] = useState<string>('');
    const [isSavingTemplate, setIsSavingTemplate] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [isFetchingRecipients, setIsFetchingRecipients] = useState(false);
    const [dryRun, setDryRun] = useState(true);
    const [sender, setSender] = useState('ankit@marketeye.in');
    const [senderName, setSenderName] = useState('Ankit Daftery');
    const [campaignResult, setCampaignResult] = useState<string | null>(null);

    const fetchCohorts = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiService.growth.getCohorts();
            if (response.success && response.data) {
                try {
                    const parsed = JSON.parse(response.data);
                    setSummaries(parsed);
                } catch (e) {
                    console.error('Failed to parse cohort data:', e);
                    setError('Failed to parse cohort data. Check server logs.');
                }
            } else {
                setError(response.error?.message || 'Failed to fetch cohorts');
            }
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to fetch cohorts');
        } finally {
            setLoading(false);
        }
    };

    const fetchTemplates = async () => {
        try {
            const response = await apiService.growth.getTemplates();
            if (response.success && response.data) {
                setTemplates(response.data);
            }
        } catch (err) {
            console.error('Failed to fetch templates', err);
        }
    };

    useEffect(() => {
        fetchCohorts();
        fetchTemplates();
    }, []);

    const handleCohortSelect = async (cohortId: string) => {
        setSelectedCohort(cohortId);
        setIsFetchingRecipients(true);
        setRecipientsCsv('');
        try {
            const response = await apiService.growth.getRecipients(cohortId);
            if (response.success && response.data) {
                setRecipientsCsv(response.data);
            }
        } catch (err) {
            console.error('Failed to fetch recipients', err);
            alert('Failed to fetch recipient list');
        } finally {
            setIsFetchingRecipients(false);
        }
    };

    const handleTemplateSelect = async (templateId: string) => {
        setSelectedTemplate(templateId);
        try {
            const response = await apiService.growth.getTemplate(templateId);
            if (response.success && response.data) {
                setTemplateContent(response.data);
            }
        } catch (err) {
            console.error('Failed to fetch template content', err);
        }
    };

    const handleSaveTemplate = async () => {
        if (!selectedTemplate) return;
        setIsSavingTemplate(true);
        try {
            await apiService.growth.saveTemplate(selectedTemplate, templateContent);
            alert('Template saved successfully!');
        } catch (err) {
            alert('Failed to save template');
        } finally {
            setIsSavingTemplate(false);
        }
    };

    const handleSendCampaign = async () => {
        if (!selectedCohort) {
            alert('Please select a cohort first');
            return;
        }

        if (!selectedTemplate) {
            alert('Please select a template first');
            return;
        }

        const confirmMsg = dryRun
            ? `Run dry run for ${selectedCohort}?`
            : `REAL SEND: Are you sure you want to send emails to ${selectedCohort}? This action cannot be undone.`;

        if (!window.confirm(confirmMsg)) return;

        setIsSending(true);
        setCampaignResult(null);
        try {
            const response = await apiService.growth.sendCampaign(selectedCohort, dryRun, recipientsCsv, sender, senderName);
            if (response.success && response.data) {
                try {
                    const stats = JSON.parse(response.data);
                    setCampaignResult(JSON.stringify(stats, null, 2));
                } catch (e) {
                    setCampaignResult(response.data);
                }
            } else {
                setCampaignResult(`Error: ${response.error?.message || 'Failed to execute campaign'}`);
            }
        } catch (err: any) {
            setCampaignResult(`Error: ${err.response?.data?.error || 'Failed to execute campaign'}`);
        } finally {
            setIsSending(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <div className="text-center">
                    <LoadingSpinner />
                    <p className="mt-4 text-slate-600 animate-pulse">Fetching latest cohort data...</p>
                </div>
            </div>
        );
    }

    // Check for access denied error
    const isAccessDenied = error?.toLowerCase().includes('access denied') || error?.toLowerCase().includes('authentication required');

    if (isAccessDenied) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center animate-fade-in">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 text-red-600 mb-6">
                    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m11-3V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2v-3zM12 9v2m0 0v2m0-2h2m-2 0H10" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                </div>
                <h1 className="text-3xl font-bold text-slate-900 mb-4">Access Denied</h1>
                <p className="text-slate-600 max-w-md mx-auto mb-8">
                    You do not have the necessary permissions to access the Growth CRM Dashboard.
                    Please contact an administrator if you believe this is an error.
                </p>
                <Link to="/dashboard" className="btn btn-primary px-8 py-3">
                    Return to Dashboard
                </Link>
            </div>
        );
    }

    const selectedSummary = summaries.find(s => s.cohort === selectedCohort);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8 animate-fade-in">
            {/* Breadcrumbs */}
            <nav className="flex items-center space-x-2 text-sm text-slate-500 mb-2">
                <Link to="/dashboard" className="hover:text-trade-primary transition-colors">Dashboard</Link>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-slate-900 font-medium">Growth CRM</span>
            </nav>

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Growth CRM Dashboard</h1>
                    <p className="text-slate-600 mt-1">Manage user cohorts and email nurturing campaigns</p>
                </div>
                <div className="flex items-center space-x-3">
                    <button
                        onClick={fetchCohorts}
                        className="btn btn-secondary shadow-sm"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <span>Refresh Data</span>
                    </button>
                </div>
            </div>

            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                    {error}
                </div>
            )}

            {/* Cohort Selection & Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-6">
                    <div className="card card-elevated p-6">
                        <h2 className="text-lg font-bold text-slate-900 mb-4">Select Cohort</h2>
                        <select
                            className="select w-full"
                            value={selectedCohort || ''}
                            onChange={(e) => handleCohortSelect(e.target.value)}
                        >
                            <option value="" disabled>Choose a cohort...</option>
                            {summaries.map(s => (
                                <option key={s.cohort} value={s.cohort}>{s.cohort}</option>
                            ))}
                        </select>
                    </div>

                    {selectedSummary && (
                        <div className="grid grid-cols-1 gap-4">
                            <div className="card card-elevated p-4 bg-blue-50 border-blue-100">
                                <p className="text-sm font-medium text-blue-600 uppercase tracking-wider">Total Users</p>
                                <p className="text-2xl font-bold text-blue-900">{selectedSummary.total_users}</p>
                            </div>
                            <div className="card card-elevated p-4 bg-green-50 border-green-100">
                                <p className="text-sm font-medium text-green-600 uppercase tracking-wider">Eligible</p>
                                <p className="text-2xl font-bold text-green-900">{selectedSummary.eligible_users}</p>
                            </div>
                            <div className="card card-elevated p-4 bg-amber-50 border-amber-100">
                                <p className="text-sm font-medium text-amber-600 uppercase tracking-wider">Blocked (7d)</p>
                                <p className="text-2xl font-bold text-amber-900">{selectedSummary.blocked_by_frequency}</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="lg:col-span-2">
                    <div className="card card-elevated flex flex-col h-full min-h-[400px]">
                        <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-slate-900">Recipient List (CSV)</h2>
                            {isFetchingRecipients && <LoadingSpinner size="sm" />}
                        </div>
                        <div className="flex-grow overflow-hidden">
                            <TemplateEditor
                                content={recipientsCsv}
                                onChange={setRecipientsCsv}
                                readOnly={!selectedCohort || isFetchingRecipients}
                            />
                        </div>
                        <div className="p-3 bg-slate-50 border-t border-slate-100 text-xs text-slate-500">
                            You can manually edit this list before sending. Ensure CSV headers remain intact.
                        </div>
                    </div>
                </div>
            </div>

            {/* Template Editor & Preview Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Editor */}
                <div className="card card-elevated flex flex-col h-[600px]">
                    <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                        <div className="flex items-center space-x-4">
                            <h2 className="text-lg font-bold text-slate-900">Template Editor</h2>
                            <select
                                className="select select-sm"
                                value={selectedTemplate || ''}
                                onChange={(e) => handleTemplateSelect(e.target.value)}
                            >
                                <option value="" disabled>Select a template</option>
                                {templates.map(t => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            onClick={handleSaveTemplate}
                            disabled={!selectedTemplate || isSavingTemplate}
                            className="btn btn-primary btn-sm"
                        >
                            {isSavingTemplate ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                    <div className="flex-grow overflow-hidden">
                        <TemplateEditor
                            content={templateContent}
                            onChange={setTemplateContent}
                            readOnly={!selectedTemplate}
                        />
                    </div>
                </div>

                {/* Preview */}
                <div className="card card-elevated flex flex-col h-[600px]">
                    <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                        <h2 className="text-lg font-bold text-slate-900">Live Preview</h2>
                    </div>
                    <div className="flex-grow bg-slate-200 overflow-hidden">
                        <EmailPreview html={templateContent} />
                    </div>
                </div>
            </div>

            {/* Campaign Execution Controls */}
            <div className="card card-elevated p-8 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="space-y-2">
                        <h2 className="text-2xl font-bold">Execute Campaign</h2>
                        <p className="text-slate-400">
                            {!selectedCohort
                                ? 'Please select a cohort first'
                                : !selectedTemplate
                                    ? 'Please select an email template'
                                    : `Ready to send to ${selectedCohort}`}
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-4">
                        <div className="flex flex-col space-y-1">
                            <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Sender Name</label>
                            <input
                                type="text"
                                value={senderName}
                                onChange={(e) => setSenderName(e.target.value)}
                                placeholder="Ankit Daftery"
                                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-white w-48"
                            />
                        </div>

                        <div className="flex flex-col space-y-1">
                            <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Sender Email</label>
                            <input
                                type="email"
                                value={sender}
                                onChange={(e) => setSender(e.target.value)}
                                placeholder="sender@marketeye.in"
                                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-white w-64"
                            />
                        </div>

                        <label className="flex items-center space-x-3 cursor-pointer group">
                            <div className="relative">
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={dryRun}
                                    onChange={(e) => setDryRun(e.target.checked)}
                                />
                                <div className={`w-12 h-6 rounded-full transition-colors ${dryRun ? 'bg-blue-500' : 'bg-slate-600'}`}></div>
                                <div className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${dryRun ? 'translate-x-6' : ''}`}></div>
                            </div>
                            <span className="font-medium text-slate-300 group-hover:text-white transition-colors">Dry Run Mode</span>
                        </label>

                        <button
                            onClick={handleSendCampaign}
                            disabled={!selectedCohort || !selectedTemplate || isSending}
                            className={`btn px-8 py-3 font-bold text-lg shadow-xl transition-all ${!selectedCohort || !selectedTemplate || isSending
                                ? 'bg-slate-700 text-slate-500 cursor-not-allowed opacity-50'
                                : dryRun
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white hover:scale-105'
                                    : 'bg-red-600 hover:bg-red-700 text-white hover:scale-105'
                                }`}
                        >
                            {isSending ? (
                                <div className="flex items-center space-x-2">
                                    <LoadingSpinner size="sm" />
                                    <span>Sending...</span>
                                </div>
                            ) : (
                                <span>{dryRun ? 'Run Simulation' : 'Execute Real Send'}</span>
                            )}
                        </button>
                    </div>
                </div>

                {campaignResult && (
                    <div className="mt-8 p-6 bg-slate-800/50 rounded-xl border border-slate-700 font-mono text-sm overflow-auto max-h-60">
                        <pre className="whitespace-pre-wrap text-slate-300">{campaignResult}</pre>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GrowthDashboard;
